import { ChangeDetectionStrategy, Component, inject, signal, viewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';

import type { NotificationDto } from 'api';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { PermissionsService } from '@auth/services/permissions.service';

import { NotificationStoreService } from '../notification-store.service';
import { routeFor } from '../notification-routing';
import { NotificationItemComponent } from './notification-item.component';
import { NotificationDetailDialogComponent } from './notification-detail-dialog.component';

/**
 * Campanita del topbar: botón con badge de no leídas y un popover con la lista paginada.
 * Orquesta el `NotificationStoreService` y la navegación por deep-link.
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    ButtonModule,
    PopoverModule,
    NotificationItemComponent,
    NotificationDetailDialogComponent,
    EmptyStateComponent,
  ],
  template: `
    <span class="relative inline-flex flex-shrink-0">
      <button
        pButton
        pRipple
        type="button"
        text
        rounded
        severity="secondary"
        title="Notificaciones"
        aria-label="Notificaciones"
        (click)="toggle($event)"
      >
        <i class="pi pi-bell"></i>
      </button>
      @if (store.unreadCount() > 0) {
        <span
          class="absolute top-0 right-0 translate-x-1/4 -translate-y-1/4 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none flex items-center justify-center pointer-events-none ring-2 ring-[var(--surface-0,#fff)] dark:ring-[var(--surface-900,#0a0a0a)] z-10"
          aria-live="polite"
        >
          {{ badgeText() }}
        </span>
      }
    </span>

    <p-popover #op styleClass="notification-popover">
      <div class="w-[22rem] max-w-[90vw]">
        <div class="flex items-center justify-between px-3 py-2 border-b border-surface-200 dark:border-surface-700">
          <span class="font-semibold">Notificaciones</span>
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="text-xs text-primary-500 hover:underline disabled:opacity-40 disabled:no-underline"
              [disabled]="store.unreadCount() === 0"
              (click)="markAllRead()"
            >
              Marcar todas
            </button>
            <button
              type="button"
              class="text-surface-400 hover:text-primary-500 p-1"
              title="Preferencias de notificaciones"
              aria-label="Preferencias de notificaciones"
              (click)="goToPreferences()"
            >
              <i class="pi pi-cog text-sm"></i>
            </button>
          </div>
        </div>

        <div class="max-h-[60vh] overflow-y-auto p-2" role="list">
          @if (store.loading()) {
            <div class="flex justify-center py-8">
              <i class="pi pi-spin pi-spinner text-2xl text-surface-400"></i>
            </div>
          } @else if (!store.hasItems()) {
            <shared-empty-state
              icon="pi pi-bell"
              title="Sin notificaciones"
              message="No tienes notificaciones por ahora."
            />
          } @else {
            @for (n of store.items(); track n.id) {
              <app-notification-item
                [notification]="n"
                (open)="open($event)"
                (remove)="remove($event)"
                (detail)="openDetail($event)"
              />
            }
            @if (store.hasNextPage()) {
              <div class="flex justify-center py-2">
                <button
                  type="button"
                  class="text-sm text-primary-500 hover:underline disabled:opacity-40"
                  [disabled]="store.loadingMore()"
                  (click)="store.loadMore()"
                >
                  {{ store.loadingMore() ? 'Cargando…' : 'Ver más' }}
                </button>
              </div>
            }
          }
        </div>
      </div>
    </p-popover>

    <app-notification-detail-dialog
      [notification]="detailNotification()"
      [visible]="detailVisible()"
      (visibleChange)="detailVisible.set($event)"
    />
  `,
})
export class NotificationBellComponent {
  readonly store = inject(NotificationStoreService);
  private readonly router = inject(Router);
  private readonly permissions = inject(PermissionsService);
  private readonly popover = viewChild.required<Popover>('op');

  /** Notificación cuyo desglose se muestra en el modal de detalle (spec §4). */
  protected readonly detailNotification = signal<NotificationDto | null>(null);
  protected readonly detailVisible = signal(false);

  /** Abre/cierra el popover y carga la primera página al abrir. */
  toggle(event: Event): void {
    this.popover().toggle(event);
    this.store.loadFirstPage();
  }

  /** Click en una notificación: marca leída, navega si tiene destino y cierra el popover. */
  open(notification: NotificationDto): void {
    if (notification.id) this.store.markRead(notification.id);
    // El destino de `SupportTicket` depende del rol: los usuarios del tenant master (agentes de
    // soporte) van al panel admin `/soporte-admin`; el resto, a la vista del tenant `/support`.
    // Usamos `isAdmin()` porque es el guard real de la ruta admin (`isDefaultTenantGuard`).
    const route = routeFor(notification, { isSupportAgent: this.permissions.isAdmin() });
    if (route) {
      this.popover().hide();
      this.router.navigate(route);
    }
  }

  remove(notification: NotificationDto): void {
    if (notification.id) this.store.remove(notification.id);
  }

  /** Abre el modal con el desglose por lista de una notificación de costo; la marca leída. */
  openDetail(notification: NotificationDto): void {
    if (notification.id) this.store.markRead(notification.id);
    this.detailNotification.set(notification);
    this.detailVisible.set(true);
  }

  markAllRead(): void {
    this.store.markAllRead();
  }

  goToPreferences(): void {
    this.popover().hide();
    this.router.navigate(['/notificaciones/preferencias']);
  }

  badgeText(): string {
    const count = this.store.unreadCount();
    return count > 99 ? '99+' : String(count);
  }
}
